import type React from 'react';
import { type FC, useEffect, useState } from 'react';

import { getSourceEventParams, useAmplitude } from '@crehana/analytics';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@crehana/compass.ui/tooltip';
import QuestionOutline from '@crehana/icons/QuestionOutline';
// biome-ignore lint/style/noRestrictedImports: legacy import, will be migrated
import { colors } from '@crehana/ui';
import { clsx } from 'clsx';
import { useRouter } from 'next/router';

import useAdminDashboard from '@/views/AdminDashboard/hooks/useAdminDashboard';

import { ThWrapper } from './TH.styles';
import type { TXProps } from './types';

const TH: FC<React.PropsWithChildren<TXProps>> = ({
  right,
  compact,
  children,
  className,
  center,
  dark,
  helpLabel,
  isFirst,
  isLast,
  border,
  tableName,
  compactHeader,
}) => {
  const router = useRouter();
  const [countHelper, setCountHelper] = useState<number>(0);
  const { logEvent } = useAmplitude();
  const { slug } = useAdminDashboard();
  const { baseLighter, baseLight } = colors;

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    countHelper >= 2 &&
      logEvent({
        event: 'Tooltips Table',
        data: {
          ...getSourceEventParams(),
          'Menu Dashboard': router.asPath.split('/')[4],
          'Menu Tab Dashboard': router.asPath.split('/')[5],
          'Table Name': tableName,
          'Tooltip Detail': helpLabel || '',
          'Count Views': countHelper,
          user: {
            'Organization Slug': slug,
          },
        },
      });
  }, [countHelper]);

  return (
    <ThWrapper
      isFirst={isFirst}
      isLast={isLast}
      border={border}
      className={clsx(
        {
          'text-center': center,
          'text-right': right,
          'text-left': !right,
          'py-0 px-16': compact || compactHeader,
          'px-12 py-0': !compact && !compactHeader,
          'tw-bg-neutral-light-400': !dark,
          'bg-gray-main-dark-mode': dark,
        },
        className,
      )}
    >
      {typeof children === 'string' ? (
        <p
          className={clsx('flex', center && 'items-center justify-center')}
          css={{ lineHeight: 0 }}
        >
          <span className={`font-label text-${dark ? 'white' : 'base-light'}`}>
            {children}
          </span>
          {helpLabel && (
            <TooltipProvider>
              <Tooltip delayDuration={300}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="w-16 h-16 ml-4"
                    onMouseLeave={() => setCountHelper(countHelper + 1)}
                  >
                    <QuestionOutline
                      size={16}
                      color={dark ? baseLighter : baseLight}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent align="center" side="top">
                  {helpLabel}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </p>
      ) : (
        children
      )}
    </ThWrapper>
  );
};

export default TH;
